<template>
  <section>
    <component
      :is="currentTabComponent"
      :client-id="currentClientId"
      :read-only="readOnly"
      :use-data-source="true"
      :client-data="getClientData"
      @saveClientData="saveClientData"
      @deleteClientData="handleDeleteClient"
      ref="componentRef"
    />
  </section>
</template>

<script lang="ts" setup>
  import { computed, ref, shallowRef, toRefs } from 'vue';
  import ClientTab from './ClientTab.vue';
  import { useClientStore } from '../client-store';
  import { ClientDataEvent } from '../client-data-event.interface';
  import AccessGuard from '@/app/shared/guards/access-guard';
  import { useAlertStore } from '@/app/shared/stores/alert-store';
  import { useI18n } from 'vue-i18n';
  import { Motif } from '@/app/shared/enums/motif-enum';

  interface ClientDetailEvents {
    (_event: 'clientCreated'): void;
    (_event: 'closeCurrentTab', _params: number): void;
    (_event: 'deleteById', _params: number | string): void;
  }

  interface ClientDetailProps {
    currentClientId: number;
    tabName: string;
  }

  const currentTabComponent = shallowRef(ClientTab);
  const props = defineProps<ClientDetailProps>();
  const emit = defineEmits<ClientDetailEvents>();
  const clientStore = useClientStore();
  const alertStore = useAlertStore();
  const { t } = useI18n();

  const { currentClientId } = toRefs(props);

  const getClientData = computed(() => {
    const clientDetail = clientStore.getClientById(currentClientId.value);
    const clientData: ClientDataEvent | undefined = clientDetail?.clientData;
    return {
      id: clientData?.id || -1,
      clientName: clientData?.clientName || '',
      description: clientData?.description || '',
      dataSources: clientData?.dataSources || [],
      createdBy: clientData?.createdBy || '',
      createdAt: clientData?.createdAt || Date.now(),
      updatedAt: clientData?.updatedAt || Date.now(),
      updatedBy: clientData?.updatedBy || '',
    };
  });

  const readOnly = computed(() => !AccessGuard.canModify('CLIENT'));

  const saveClientData = (clientData: ClientDataEvent) => {
    if (clientData.id == -1) {
      saveData(clientData);
    } else {
      updateData(clientData);
    }
  };

  const saveData = async (clientData: ClientDataEvent) => {
    try {
      await clientStore.addClient(clientData);
      emit('closeCurrentTab', currentClientId.value);
    } catch (error: any) {
      console.error(error);
      alertStore.showNotification(error.message, t('client'), Motif.ERROR);
    }
  };

  const updateData = async (clientData: ClientDataEvent) => {
    try {
      await clientStore.updateClient(currentClientId.value, clientData);
      emit('closeCurrentTab', currentClientId.value);
    } catch (error: any) {
      console.error(error);
      alertStore.showNotification(error.message, t('client'), Motif.ERROR);
    }
  };

  const handleDeleteClient = (clientId: number | string) => {
    emit('deleteById', clientId);
  };
</script>
